// Property-rent-ledger endpoint functions.
//
// Accept camelCase view-model inputs, return camelCase view models. Throw
// AppError (the httpClient interceptor normalises) — never wrap in Result<T>.

import { adminApi } from '@core/api/httpClient'
import type { PaginatedResponse } from '@shared/types/pagination'
import { RENT_LEDGER_ENDPOINT_PATHS } from './rentLedgerEndpointPaths'
import type {
  CreatedPropertyBillDto,
  PropertyRentLedgerDetailDto,
  RentLedgerListDto,
  BillPaymentPermissionDto,
} from './rentLedgerContracts'
import {
  parsePropertyRentLedgerDetail,
  parseRentLedgerList,
  parseBillPaymentPermission,
} from './rentLedgerParser'
import type {
  CreatePropertyBillInput,
  PropertyRentLedgerDetail,
  PropertyRentLedgerRow,
  RentLedgerFilters,
  VoidPropertyBillInput,
  BillPaymentPermission,
} from '../types/rentLedgerTypes'

/**
 * Every admin-service response is wrapped by its global transform interceptor
 * as `{ success, statusCode, message, data }`. Strip that one level here so no
 * parser ever sees the transport envelope — including list responses, whose
 * payload is itself `{ data, meta }`.
 */
function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

/**
 * Status is an array — the queue filters to several at once — and axios would
 * serialise it as `status[]=`. A comma-joined value is what the backend's
 * transform expects.
 */
function toQueryParams(filters: RentLedgerFilters): Record<string, string> {
  const params: Record<string, string> = {}

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length > 0) params[key] = value.join(',')
      continue
    }
    params[key] = String(value)
  }

  return params
}

export async function getRentLedger(
  filters: RentLedgerFilters,
): Promise<PaginatedResponse<PropertyRentLedgerRow>> {
  const { data } = await adminApi.get<RentLedgerListDto | { data: RentLedgerListDto }>(
    RENT_LEDGER_ENDPOINT_PATHS.LIST,
    { params: toQueryParams(filters) },
  )
  return parseRentLedgerList(unwrap(data))
}

export async function getPropertyRentLedger(propertyId: string): Promise<PropertyRentLedgerDetail> {
  const { data } = await adminApi.get<
    PropertyRentLedgerDetailDto | { data: PropertyRentLedgerDetailDto }
  >(RENT_LEDGER_ENDPOINT_PATHS.DETAIL(propertyId))
  return parsePropertyRentLedgerDetail(unwrap(data))
}

export async function createPropertyBill(input: CreatePropertyBillInput): Promise<string> {
  const { data } = await adminApi.post<CreatedPropertyBillDto | { data: CreatedPropertyBillDto }>(
    RENT_LEDGER_ENDPOINT_PATHS.CREATE_BILL,
    input,
  )
  return unwrap(data).id
}

/**
 * Whether the signed-in admin may pay this bill, and why not.
 *
 * Read separately from the bill itself for the same reason as the
 * distribution-run permission queries — the answer depends on WHO is asking.
 */
export async function getBillPaymentPermission(billId: string): Promise<BillPaymentPermission> {
  const { data } = await adminApi.get<
    BillPaymentPermissionDto | { data: BillPaymentPermissionDto }
  >(RENT_LEDGER_ENDPOINT_PATHS.BILL_PAYMENT_PERMISSION(billId))
  return parseBillPaymentPermission(unwrap(data))
}

/** Takes the money out of the reserve pot. Not reversible from this screen. */
export async function payPropertyBill(billId: string): Promise<void> {
  await adminApi.post(RENT_LEDGER_ENDPOINT_PATHS.PAY_BILL(billId))
}

export async function voidPropertyBill(input: VoidPropertyBillInput): Promise<void> {
  await adminApi.post(RENT_LEDGER_ENDPOINT_PATHS.VOID_BILL(input.billId), {
    reason: input.reason,
  })
}
